import { fromJS } from "immutable";
import {
  calculateDataGridItemAges,
  getCurrentYear,
  updateStartAgeOrEndAge,
  updateStartYearOrEndYear,
} from "./shared";
import { process } from "@progress/kendo-data-query";
import { LATEST_YEAR, EXPENSE_ROW, GOAL_ROW } from "./constants";
import { isDatePriorToTodaysMonth, isNumericTextboxValid } from "./validation";

export const DefaultGridReducer = (
  collectionField,
  state,
  setState,
  clientInformation,
  scopeType
) => {
  return {
    clientInformation: clientInformation,
    editDataItem: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...item,
            inEdit: true,
          };
        }),
      });
    },
    deleteDataItem: (dataItem) => {
      const collection = state[collectionField].filter((item) => {
        return item.editIndex !== dataItem.editIndex;
      });

      setState({
        ...state,
        [collectionField]: collection,
        [`${collectionField}_backup`]: fromJS(
          collection.filter((item) => !item.isNew)
        ),
      });
    },
    cancelDataItem: (dataItem) => {
      const backupItem = state[`${collectionField}_backup`]
        .toJS()
        .find((item) => {
          return item.editIndex === dataItem.editIndex;
        });

      if (!backupItem) {
        setState({
          ...state,
          [collectionField]: state[collectionField].filter((item) => {
            return item.editIndex !== dataItem.editIndex;
          }),
        });
        return;
      }

      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...backupItem,
            inEdit: false,
          };
        }),
      });
    },
    saveDataItem: (dataItem, newItem) => {
      let isValid = true;

      const validationCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;

        const currentYear = getCurrentYear();

        if (item.start && item.end) {
          if (
            item.startYear > item.endYear ||
            item.startYear < currentYear ||
            item.endYear < currentYear ||
            item.startYear > LATEST_YEAR ||
            item.endYear > LATEST_YEAR ||
            item.start > item.end ||
            !item.endDateValid
          ) {
            item.invalidFields = ["start", "end", "startAge", "endAge"];
            isValid = false;
          }
        }

        if (item.inflationRate < 0 || item.inflationRate > 1.0) {
          if (item.invalidFields) {
            item.invalidFields.push("inflationRate");
          } else {
            item.invalidFields = ["inflationRate"];
          }
          isValid = false;
        }

        if (item.deathBenefit && item.deathBenefit > 100000000) {
          if (item.invalidFields) {
            item.invalidFields.push("deathBenefit");
          } else {
            item.invalidFields = ["deathBenefit"];
          }

          isValid = false;
        }

        if (item.name.length < 1 || item.name.length > 30) {
          if (item.invalidFields) {
            item.invalidFields.push("name");
          } else {
            item.invalidFields = ["name"];
          }
          isValid = false;
        }

        if (
          ((item.rowType === EXPENSE_ROW || item.rowType === GOAL_ROW) &&
            item.amount < 1) ||
          item.amount > 1000000000
        ) {
          if (item.invalidFields) {
            item.invalidFields.push("amount");
          } else {
            item.invalidFields = ["amount"];
          }
          isValid = false;
        }

        return item;
      });

      if (!isValid) {
        setState({
          ...state,
          [collectionField]: validationCollection,
        });
        return false;
      }

      const newCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;
        return {
          ...item,
          invalidFields: [],
          inEdit: false,
          isExpanded: false,
          isNew: false,
          isEdited: true,
        };
      });

      if (newItem) {
        const currentIndex = Math.max(...newCollection.map((e) => e.editIndex));
        newItem.editIndex = currentIndex >= 0 ? currentIndex + 1 : 0;
        newCollection.push(newItem);
      }

      const backupField = `${collectionField}_backup`;

      setState({
        ...state,
        [collectionField]: newCollection,
        [backupField]: fromJS(newCollection.filter((item) => !item.isNew)),
      });

      return true;
    },
    onItemChange: (event) => {
      const dataItem = event.dataItem;
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) {
            if (event.field === "isReinvest") {
              return {
                ...item,
                isReinvest: false,
              };
            }
            return item;
          }


          const field = event.field;

          let newData = { ...item };

          if (field === "start" || field === "end")
            return updateStartYearOrEndYear(event, newData, clientInformation);
          if (field === "startAge" || field === "endAge")
            return updateStartAgeOrEndAge(event, newData, clientInformation);

          newData.isEdited = true;
          if (field === "startYear") {
            newData[field] = event.value;
            newData["start"] = new Date(newData["startYear"], newData["startMonth"], 1);
            newData["startDateValid"] = newData["start"] <= newData["end"];
            newData["endDateValid"] = newData["start"] <= newData["end"];
            return newData;
          }

          if (field === "endYear") {
            newData[field] = event.value;
            newData["end"] = new Date(newData["endYear"], newData["endMonth"], 1);
            newData["startDateValid"] = newData["start"] <= newData["end"];
            newData["endDateValid"] = newData["start"] <= newData["end"];
            return newData;
          }

          if (field === "startMonth") {
            newData[field] = event.value;
            newData["start"] = new Date(newData["startYear"], newData["startMonth"], 1);
            newData["startDateValid"] = newData["start"] <= newData["end"];
            newData["endDateValid"] = newData["start"] <= newData["end"];
            return newData;
          }

          if (field === "endMonth") {
            newData[field] = event.value;
            newData["end"] = new Date(newData["endYear"], newData["endMonth"], 1);
            newData["startDateValid"] = newData["start"] <= newData["end"];
            newData["endDateValid"] = newData["start"] <= newData["end"];
            return newData;
          }


          if (typeof newData[field] === "boolean") {
            newData[field] = event.value ? event.value : !newData[field];
          } else {
            newData[field] = event.value;
          }

          if (field === "appliesTo") {
            newData = calculateDataGridItemAges(newData, clientInformation);
            newData.appliesToName = newData.appliesTo.name;
          }

          return newData;
        }),
      });
    },
  };
};

export const PlanTaxDataGridReducer = (
  collectionField,
  state,
  setState,
  clientInformation,
  scopeType
) => {
  return {
    clientInformation: clientInformation,
    editDataItem: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...item,
            inEdit: true,
          };
        }),
      });
    },
    deleteDataItem: (dataItem) => {
      const collection = state[collectionField].filter((item) => {
        return item.editIndex !== dataItem.editIndex;
      });

      setState({
        ...state,
        [collectionField]: collection,
        [`${collectionField}_backup`]: fromJS(
          collection.filter((item) => !item.isNew)
        ),
      });
    },
    cancelDataItem: (dataItem) => {
      const backupItem = state[`${collectionField}_backup`]
        .toJS()
        .find((item) => {
          return item.editIndex === dataItem.editIndex;
        });

      if (!backupItem) {
        setState({
          ...state,
          [collectionField]: state[collectionField].filter((item) => {
            return item.editIndex !== dataItem.editIndex;
          }),
        });
        return;
      }

      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...backupItem,
            inEdit: false,
          };
        }),
      });
    },
    saveDataItem: (dataItem, newItem) => {
      let isValid = true;

      const validationCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;

        const currentYear = getCurrentYear();
        if (
          item.startYear > item.endYear ||
          item.startYear < currentYear ||
          item.endYear < currentYear ||
          item.startYear > LATEST_YEAR ||
          item.endYear > LATEST_YEAR ||
          item.start > item.end ||
          !item.endDateValid ||
          isDatePriorToTodaysMonth(item.start)
        ) {
          item.invalidFields = ["start", "end", "startAge", "endAge"];
          isValid = false;
        }

        if (item.inflationRate < 0 || item.inflationRate > 1.0) {
          if (item.invalidFields) {
            item.invalidFields.push("inflationRate");
          } else {
            item.invalidFields = ["inflationRate"];
          }
          isValid = false;
        }

        if (item.deathBenefit && item.deathBenefit > 100000000) {
          if (item.invalidFields) {
            item.invalidFields.push("deathBenefit");
          } else {
            item.invalidFields = ["deathBenefit"];
          }

          isValid = false;
        }

        if (item.name.length < 1 || item.name.length > 30) {
          if (item.invalidFields) {
            item.invalidFields.push("name");
          } else {
            item.invalidFields = ["name"];
          }
          isValid = false;
        }

        if (
          ((item.rowType === EXPENSE_ROW || item.rowType === GOAL_ROW) &&
            item.amount < 1) ||
          item.amount > 1000000000
        ) {
          if (item.invalidFields) {
            item.invalidFields.push("amount");
          } else {
            item.invalidFields = ["amount"];
          }
          isValid = false;
        }

        return item;
      });

      if (!isValid) {
        setState({
          ...state,
          [collectionField]: validationCollection,
        });
        return false;
      }

      const newCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;
        return {
          ...item,
          invalidFields: [],
          inEdit: false,
          isExpanded: false,
          isNew: false,
          isEdited: true,
        };
      });

      if (newItem) {
        const currentIndex = Math.max(...newCollection.map((e) => e.editIndex));
        newItem.editIndex = currentIndex >= 0 ? currentIndex + 1 : 0;
        newCollection.push(newItem);
      }

      const backupField = `${collectionField}_backup`;

      setState({
        ...state,
        [collectionField]: newCollection,
        [backupField]: fromJS(newCollection.filter((item) => !item.isNew)),
      });

      return true;
    },
    onItemChange: (event) => {
      const dataItem = event.dataItem;
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item, index) => {
          if (index === 1) {
            if (state[collectionField][0]["deductionType"].key === "ITEMIZED") {
              return {
                ...item,
                lockedFields: ["deductionType", "start", "end"],
              };
            }

            return {
              ...item,
              lockedFields: [
                "deductionType",
                "start",
                "end",
                "amount",
                "inflation",
              ],
            };
          }

          if (item.editIndex !== dataItem.editIndex) {
            return item;
          }

          const field = event.field;
          let newData = { ...item };
          newData.isEdited = true;
          newData[field] = event.value;

          if (field === "deductionType") {
            if (newData[field].key === "ITEMIZED") {
              newData["lockedFields"] = ["start", "end"];
            } else {
              newData["lockedFields"] = ["start", "end", "amount", "inflation"];
            }
          }

          return newData;
        }),
      });
    },
  };
};

export const ClientListReducer = (state, setState) => {
  const createDataState = (dataState, data) => {
    return {
      result: process(data, dataState),
      dataState: dataState,
    };
  };

  return {
    onDataStateChange: (event) => {
      setState({
        ...state,
        dataState: createDataState(event.dataState, state.data),
      });
    },
    createDataState: createDataState,
    updateTake: (take) => {
      setState({
        ...state,
        dataState: createDataState(
          {
            ...state.dataState.dataState,
            take: take,
          },
          state.data
        ),
      });
    },
    clearFilters: () => {
      let newState = { ...state };
      delete newState.dataState.dataState.filter;
      newState.dataState = createDataState(
        {
          take: state.dataState.dataState.take,
          skip: 0,
        },
        state.data
      );
      setState(newState);
    },
    hasFiltersApplied: () => {
      return state.dataState.dataState.filter ? true : false;
    },
  };
};

export const TaxDataGridReducer = (
  collectionField,
  state,
  setState,
  clientInformation
) => {
  return {
    clientInformation: clientInformation,
    onItemChange: (event) => {
      const dataItem = event.dataItem;
      setState({
        ...state,
        [collectionField]: state[collectionField].map((tax) => {
          if (tax.editIndex !== dataItem.editIndex) return tax;
          return {
            ...tax,
            isExpanded: event.value,
          };
        }),
      });
    },
    toggleEditMode: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].map((tax) => {
          if (tax.editIndex !== dataItem.editIndex) return tax;
          return {
            ...tax,
            inEdit: !tax.inEdit,
            isExpanded: true,
            data: tax.data.map((taxItem) => {
              return {
                ...taxItem,
                inEdit: !tax.inEdit,
              };
            }),
          };
        }),
      });
    },
    saveTaxChanges: (dataItem) => {
      let isValid = true;
      state[collectionField].forEach((tax) => {
        tax.data.forEach((item) => {
          if (!isNumericTextboxValid(item.taxRate, 0, 1)) {
            isValid = false;
          }
        });
      });

      if (!isValid) {
        return false;
      }

      const newCollection = state[collectionField]
        .map((tax) => {
          if (tax.editIndex !== dataItem.editIndex) return tax;
          return {
            ...tax,
            inEdit: !tax.inEdit,
            isExpanded: true,
            data: tax.data.map((taxItem) => {
              return {
                ...taxItem,
                inEdit: !tax.inEdit,
              };
            }),
          };
        })
        .map((tax) => {
          if (tax.editIndex !== dataItem.editIndex) return tax;
          return {
            ...tax,
            backup: fromJS(tax.data),
          };
        });

      setState({
        ...state,
        [collectionField]: newCollection,
      });

      return true;
    },
    cancelTaxChanges: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].map((tax) => {
          if (tax.editIndex !== dataItem.editIndex) return tax;
          return {
            ...tax,
            inEdit: false,
            data: tax.backup.toJS(),
          };
        }),
      });
    },
    updateTaxList: (parentItem) => {
      return (dataList) => {
        setState({
          ...state,
          [collectionField]: state[collectionField].map((tax) => {
            if (tax.editIndex !== parentItem.editIndex) return tax;
            return {
              ...tax,
              data: dataList,
            };
          }),
        });
      };
    },
  };
};

export const MarginalTaxBracketsReducer = (
  collectionField,
  inflationRateField,
  state,
  setState
) => {
  return {
    addTaxBracket: () => {
      const editIndex = Math.max(
        ...state[collectionField].map((c) => c.editIndex)
      );

      const numberOfBrackets = state[collectionField].length;
      let lastBracket = state[collectionField][numberOfBrackets - 1];
      if (lastBracket.endAmount == null) {
        lastBracket.endAmount = lastBracket.startAmount + 1;
      }

      const newBracket = {
        id: null,
        editIndex: editIndex >= 0 ? editIndex + 1 : 0,
        inEdit: true,
        taxRate: 0.0,
        startAmount: lastBracket.endAmount + 1,
        endAmount: lastBracket.endAmount + 2,
        isLast: true,
      };

      let bracketList = state[collectionField].map((item) => {
        delete item.isLast;
        return item;
      });
      bracketList.splice(numberOfBrackets - 1, 1, lastBracket, newBracket);

      setState({
        ...state,
        [collectionField]: bracketList,
      });
    },
    deleteTaxBracket: (dataItem) => {
      const bracketList = state[collectionField]
        .filter((item) => {
          return item.editIndex !== dataItem.editIndex;
        })
        .map((item) => {
          delete item.isLast;
          return item;
        });
      bracketList[bracketList.length - 1].isLast = true;

      setState({
        ...state,
        [collectionField]: bracketList,
      });
    },
    updateTaxBracket: (event) => {
      const dataItem = event.dataItem;

      let nextState = {
        ...state,
        [collectionField]: state[collectionField].map((bracket) => {
          if (bracket.editIndex !== dataItem.editIndex) return bracket;
          return {
            ...bracket,
            [event.field]: event.value,
          };
        }),
      };

      let hasErrorAlready = false;
      nextState = {
        ...nextState,
        [collectionField]: nextState[collectionField].map((bracket, index) => {
          if (index === 0) return bracket;
          const previous = nextState[collectionField][index - 1];
          let current = {
            ...bracket,
            startAmount: previous.endAmount + 1,
          };
          current.invalidFields = null;
          if (current.endAmount <= current.startAmount) {
            current.invalidFields = ["endAmount"];
            hasErrorAlready = true;
          }
          return current;
        }),
      };

      setState(nextState);
    },
    onInflationRateChange: (event) => {
      setState({
        ...state,
        [inflationRateField]: event.target.value,
      });
    },
  };
};

export const FilingStatusReducer = (
  collectionField,
  taxDeductionData,
  state,
  setState,
  clientInformation
) => {
  return {
    clientInformation: clientInformation,
    editDataItem: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...item,
            inEdit: true,
          };
        }),
      });
    },
    deleteDataItem: (dataItem) => {
      setState({
        ...state,
        [collectionField]: state[collectionField].filter((item) => {
          return item.editIndex !== dataItem.editIndex;
        }),
      });
    },
    cancelDataItem: (dataItem) => {
      const backupItem = state[`${collectionField}_backup`]
        .toJS()
        .find((item) => {
          return item.editIndex === dataItem.editIndex;
        });

      if (!backupItem) {
        setState({
          ...state,
          [collectionField]: state[collectionField].filter((item) => {
            return item.editIndex !== dataItem.editIndex;
          }),
        });
        return;
      }

      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) return item;
          return {
            ...backupItem,
            inEdit: false,
          };
        }),
      });
    },
    saveDataItem: (dataItem) => {
      let isValid = true;

      const validationCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;

        if (!isNumericTextboxValid(item.inflation, 0, 1)) {
          if (item.invalidFields) {
            item.invalidFields.push("inflation");
          } else {
            item.invalidFields = ["inflation"];
          }
          isValid = false;
        }

        return item;
      });

      if (!isValid) {
        setState({
          ...state,
          [collectionField]: validationCollection,
        });
        return false;
      }

      const newCollection = state[collectionField].map((item) => {
        if (item.editIndex !== dataItem.editIndex) return item;
        return {
          ...item,
          invalidFields: [],
          inEdit: false,
          isExpanded: false,
        };
      });

      const backupField = `${collectionField}_backup`;

      setState({
        ...state,
        [collectionField]: newCollection,
        [backupField]: fromJS(newCollection),
      });

      return true;
    },
    onItemChange: (event) => {
      const dataItem = event.dataItem;
      setState({
        ...state,
        [collectionField]: state[collectionField].map((item) => {
          if (item.editIndex !== dataItem.editIndex) {
            return item;
          }

          const field = event.field;
          let newData = { ...item };

          if (field === "maritalStatus") {
            const filingStatus = taxDeductionData.filter((t) => {
              return t.maritalStatus === event.value.key;
            })[0];

            newData.maritalStatus = event.value;
            newData.inflation = filingStatus.inflation / 100;
            newData.annualDeduction = filingStatus.annualDeduction;
            return newData;
          }

          newData.isEdited = true;
          if (typeof newData[field] === "boolean") {
            newData[field] = event.value ? event.value : !newData[field];
          } else {
            newData[field] = event.value;
          }
          return newData;
        }),
      });
    },
  };
};
